/**
 * Deep-stack series for the QXO roofing board.
 * One unit per chart. Stocks are not mixed with SAAR flows.
 * FRED pull 21 Sep 2026. Quarterly = average of months in the quarter.
 */

/** Census 1-family vs 5+ unit starts, SAAR millions. 2–4 unit starts omitted (Aug 26: 13k). */
export const STRUCTURE_STARTS = [
  { period: 'Q1 19', sf: 0.844, mf: 0.327 },
  { period: 'Q2 19', sf: 0.866, mf: 0.389 },
  { period: 'Q3 19', sf: 0.908, mf: 0.388 },
  { period: 'Q4 19', sf: 0.937, mf: 0.455 },
  { period: 'Q1 20', sf: 0.961, mf: 0.491 },
  { period: 'Q2 20', sf: 0.777, mf: 0.293 },
  { period: 'Q3 20', sf: 1.054, mf: 0.398 },
  { period: 'Q4 20', sf: 1.21, mf: 0.343 },
  { period: 'Q1 21', sf: 1.128, mf: 0.426 },
  { period: 'Q2 21', sf: 1.109, mf: 0.464 },
  { period: 'Q3 21', sf: 1.117, mf: 0.465 },
  { period: 'Q4 21', sf: 1.164, mf: 0.494 },
  { period: 'Q1 22', sf: 1.177, mf: 0.512 },
  { period: 'Q2 22', sf: 1.089, mf: 0.531 },
  { period: 'Q3 22', sf: 0.91, mf: 0.537 },
  { period: 'Q4 22', sf: 0.839, mf: 0.541 },
  { period: 'Q1 23', sf: 0.826, mf: 0.532 },
  { period: 'Q2 23', sf: 0.937, mf: 0.508 },
  { period: 'Q3 23', sf: 0.981, mf: 0.393 },
  { period: 'Q4 23', sf: 1.045, mf: 0.407 },
  { period: 'Q1 24', sf: 1.062, mf: 0.334 },
  { period: 'Q2 24', sf: 1.008, mf: 0.32 },
  { period: 'Q3 24', sf: 0.982, mf: 0.334 },
  { period: 'Q4 24', sf: 1.008, mf: 0.36 },
  { period: 'Q1 25', sf: 1.012, mf: 0.363 },
  { period: 'Q2 25', sf: 0.941, mf: 0.394 },
  { period: 'Q3 25', sf: 0.89, mf: 0.444 },
  { period: 'Q4 25', sf: 0.923, mf: 0.38 },
  { period: 'Q1 26', sf: 0.945, mf: 0.455 },
  { period: 'Q2 26', sf: 0.902, mf: 0.432 },
  { period: 'Q3 26*', sf: 0.885, mf: 0.394 },
];

export const STRUCTURE_LATEST = {
  month: 'Aug 2026',
  sf: 0.918,
  sfYoy: 5.2,
  mf: 0.344,
  mfYoy: -15.5,
} as const;

/** 1-family permits / starts / completions. Same Census SAAR millions as the total funnel, SF slice only. */
export const SF_FUNNEL = [
  { period: 'Q1 19', permit: 0.801, starts: 0.844, complete: 0.875 },
  { period: 'Q2 19', permit: 0.826, starts: 0.866, complete: 0.893 },
  { period: 'Q3 19', permit: 0.892, starts: 0.908, complete: 0.91 },
  { period: 'Q4 19', permit: 0.937, starts: 0.937, complete: 0.937 },
  { period: 'Q1 20', permit: 0.941, starts: 0.961, complete: 0.905 },
  { period: 'Q2 20', permit: 0.76, starts: 0.777, complete: 0.873 },
  { period: 'Q3 20', permit: 1.077, starts: 1.054, complete: 0.93 },
  { period: 'Q4 20', permit: 1.178, starts: 1.21, complete: 0.934 },
  { period: 'Q1 21', permit: 1.173, starts: 1.128, complete: 1.006 },
  { period: 'Q2 21', permit: 1.114, starts: 1.109, complete: 0.95 },
  { period: 'Q3 21', permit: 1.067, starts: 1.117, complete: 0.948 },
  { period: 'Q4 21', permit: 1.12, starts: 1.164, complete: 0.978 },
  { period: 'Q1 22', permit: 1.185, starts: 1.177, complete: 1.015 },
  { period: 'Q2 22', permit: 1.026, starts: 1.089, complete: 1.014 },
  { period: 'Q3 22', permit: 0.894, starts: 0.91, complete: 1.026 },
  { period: 'Q4 22', permit: 0.789, starts: 0.839, complete: 1.028 },
  { period: 'Q1 23', permit: 0.796, starts: 0.826, complete: 1.034 },
  { period: 'Q2 23', permit: 0.919, starts: 0.937, complete: 0.987 },
  { period: 'Q3 23', permit: 0.976, starts: 0.981, complete: 0.981 },
  { period: 'Q4 23', permit: 0.995, starts: 1.045, complete: 0.997 },
  { period: 'Q1 24', permit: 1.013, starts: 1.062, complete: 0.977 },
  { period: 'Q2 24', permit: 0.966, starts: 1.008, complete: 1.076 },
  { period: 'Q3 24', permit: 0.963, starts: 0.982, complete: 1.038 },
  { period: 'Q4 24', permit: 0.973, starts: 1.008, complete: 0.973 },
  { period: 'Q1 25', permit: 0.975, starts: 1.012, complete: 1.039 },
  { period: 'Q2 25', permit: 0.899, starts: 0.941, complete: 0.978 },
  { period: 'Q3 25', permit: 0.88, starts: 0.89, complete: 1.003 },
  { period: 'Q4 25', permit: 0.895, starts: 0.923, complete: 0.996 },
  { period: 'Q1 26', permit: 0.901, starts: 0.945, complete: 0.918 },
  { period: 'Q2 26', permit: 0.882, starts: 0.902, complete: 0.91 },
  { period: 'Q3 26*', permit: 0.886, starts: 0.885, complete: 0.864 },
];

export const SF_FUNNEL_LATEST = {
  month: 'Aug 2026',
  permit: 0.878,
  permitYoy: 1.3,
  starts: 0.918,
  startsYoy: 5.2,
  complete: 0.816,
  completeYoy: -22.9,
} as const;

/**
 * 1-family starts vs new 1-family homes sold. Both Census SAAR millions of units.
 * Sales lag starts; the gap is builder inventory, not shingle squares.
 */
export const STARTS_VS_SALES = [
  { period: 'Q1 19', starts: 0.844, sold: 0.654 },
  { period: 'Q2 19', starts: 0.866, sold: 0.683 },
  { period: 'Q3 19', starts: 0.908, sold: 0.691 },
  { period: 'Q4 19', starts: 0.937, sold: 0.712 },
  { period: 'Q1 20', starts: 0.961, sold: 0.674 },
  { period: 'Q2 20', starts: 0.777, sold: 0.728 },
  { period: 'Q3 20', starts: 1.054, sold: 0.992 },
  { period: 'Q4 20', starts: 1.21, sold: 0.927 },
  { period: 'Q1 21', starts: 1.128, sold: 0.841 },
  { period: 'Q2 21', starts: 1.109, sold: 0.749 },
  { period: 'Q3 21', starts: 1.117, sold: 0.722 },
  { period: 'Q4 21', starts: 1.164, sold: 0.762 },
  { period: 'Q1 22', starts: 1.177, sold: 0.768 },
  { period: 'Q2 22', starts: 1.089, sold: 0.605 },
  { period: 'Q3 22', starts: 0.91, sold: 0.574 },
  { period: 'Q4 22', starts: 0.839, sold: 0.598 },
  { period: 'Q1 23', starts: 0.826, sold: 0.642 },
  { period: 'Q2 23', starts: 0.937, sold: 0.693 },
  { period: 'Q3 23', starts: 0.981, sold: 0.685 },
  { period: 'Q4 23', starts: 1.045, sold: 0.643 },
  { period: 'Q1 24', starts: 1.062, sold: 0.672 },
  { period: 'Q2 24', starts: 1.008, sold: 0.686 },
  { period: 'Q3 24', starts: 0.982, sold: 0.711 },
  { period: 'Q4 24', starts: 1.008, sold: 0.668 },
  { period: 'Q1 25', starts: 1.012, sold: 0.654 },
  { period: 'Q2 25', starts: 0.941, sold: 0.663 },
  { period: 'Q3 25', starts: 0.89, sold: 0.687 },
  { period: 'Q4 25', starts: 0.923, sold: 0.711 },
  { period: 'Q1 26', starts: 0.945, sold: 0.622 },
  { period: 'Q2 26', starts: 0.902, sold: 0.65 },
  { period: 'Q3 26*', starts: 0.885, sold: 0.607 },
];

export const NEW_HOME_LATEST = {
  month: 'Jul 2026',
  sold: 0.607,
  soldYoy: -6.3,
  mos: 9.6,
  mosYoy: 4.3,
} as const;

export const NEW_HOME_MOS = [
  { period: 'Q1 19', mos: 6.3 },
  { period: 'Q2 19', mos: 5.87 },
  { period: 'Q3 19', mos: 5.67 },
  { period: 'Q4 19', mos: 5.4 },
  { period: 'Q1 20', mos: 5.9 },
  { period: 'Q2 20', mos: 5.37 },
  { period: 'Q3 20', mos: 3.47 },
  { period: 'Q4 20', mos: 3.77 },
  { period: 'Q1 21', mos: 4.4 },
  { period: 'Q2 21', mos: 5.37 },
  { period: 'Q3 21', mos: 6.17 },
  { period: 'Q4 21', mos: 6.13 },
  { period: 'Q1 22', mos: 6.27 },
  { period: 'Q2 22', mos: 8.7 },
  { period: 'Q3 22', mos: 9.73 },
  { period: 'Q4 22', mos: 9.23 },
  { period: 'Q1 23', mos: 8.17 },
  { period: 'Q2 23', mos: 7.4 },
  { period: 'Q3 23', mos: 7.57 },
  { period: 'Q4 23', mos: 8.4 },
  { period: 'Q1 24', mos: 8.27 },
  { period: 'Q2 24', mos: 8.13 },
  { period: 'Q3 24', mos: 7.9 },
  { period: 'Q4 24', mos: 8.77 },
  { period: 'Q1 25', mos: 9.23 },
  { period: 'Q2 25', mos: 9.1 },
  { period: 'Q3 25', mos: 8.63 },
  { period: 'Q4 25', mos: 8.2 },
  { period: 'Q1 26', mos: 9.3 },
  { period: 'Q2 26', mos: 8.93 },
  { period: 'Q3 26*', mos: 9.6 },
];

/**
 * Census stocks, millions of units — not SAAR flows.
 * Under construction is seasonally adjusted (UNDCONTSA).
 * Authorized but not started is the Census level (AUTHNOTT), NSA.
 */
export const HOUSING_BACKLOG = [
  { period: 'Q1 19', under: 1.138, notStarted: 0.189 },
  { period: 'Q2 19', under: 1.133, notStarted: 0.175 },
  { period: 'Q3 19', under: 1.152, notStarted: 0.168 },
  { period: 'Q4 19', under: 1.167, notStarted: 0.184 },
  { period: 'Q1 20', under: 1.206, notStarted: 0.168 },
  { period: 'Q2 20', under: 1.185, notStarted: 0.17 },
  { period: 'Q3 20', under: 1.214, notStarted: 0.17 },
  { period: 'Q4 20', under: 1.246, notStarted: 0.18 },
  { period: 'Q1 21', under: 1.289, notStarted: 0.218 },
  { period: 'Q2 21', under: 1.344, notStarted: 0.253 },
  { period: 'Q3 21', under: 1.414, notStarted: 0.253 },
  { period: 'Q4 21', under: 1.495, notStarted: 0.259 },
  { period: 'Q1 22', under: 1.585, notStarted: 0.281 },
  { period: 'Q2 22', under: 1.679, notStarted: 0.292 },
  { period: 'Q3 22', under: 1.696, notStarted: 0.298 },
  { period: 'Q4 22', under: 1.703, notStarted: 0.292 },
  { period: 'Q1 23', under: 1.684, notStarted: 0.3 },
  { period: 'Q2 23', under: 1.687, notStarted: 0.287 },
  { period: 'Q3 23', under: 1.692, notStarted: 0.27 },
  { period: 'Q4 23', under: 1.685, notStarted: 0.271 },
  { period: 'Q1 24', under: 1.656, notStarted: 0.273 },
  { period: 'Q2 24', under: 1.59, notStarted: 0.278 },
  { period: 'Q3 24', under: 1.519, notStarted: 0.282 },
  { period: 'Q4 24', under: 1.443, notStarted: 0.277 },
  { period: 'Q1 25', under: 1.405, notStarted: 0.279 },
  { period: 'Q2 25', under: 1.368, notStarted: 0.271 },
  { period: 'Q3 25', under: 1.326, notStarted: 0.248 },
  { period: 'Q4 25', under: 1.298, notStarted: 0.255 },
  { period: 'Q1 26', under: 1.267, notStarted: 0.264 },
  { period: 'Q2 26', under: 1.259, notStarted: 0.271 },
  { period: 'Q3 26*', under: 1.269, notStarted: 0.277 },
];

export const BACKLOG_LATEST = {
  month: 'Aug 2026',
  under: 1.271,
  underYoy: -3.2,
  notStarted: 0.276,
  notStartedYoy: 12.6,
} as const;

/** Census C30 put-in-place, $ billions SAAR. Residential is the steep/SF dollar book; nonresidential is commercial. */
export const PUT_IN_PLACE = [
  { period: 'Q1 19', resi: 517.2, nonres: 465.9 },
  { period: 'Q2 19', resi: 530.2, nonres: 489.1 },
  { period: 'Q3 19', resi: 558.6, nonres: 516.4 },
  { period: 'Q4 19', resi: 579.2, nonres: 528.4 },
  { period: 'Q1 20', resi: 606.5, nonres: 518.2 },
  { period: 'Q2 20', resi: 589.4, nonres: 494.1 },
  { period: 'Q3 20', resi: 638.6, nonres: 487.5 },
  { period: 'Q4 20', resi: 705.5, nonres: 483.3 },
  { period: 'Q1 21', resi: 745.9, nonres: 486.1 },
  { period: 'Q2 21', resi: 779.2, nonres: 490.0 },
  { period: 'Q3 21', resi: 813.7, nonres: 497.3 },
  { period: 'Q4 21', resi: 858.6, nonres: 513.1 },
  { period: 'Q1 22', resi: 934.6, nonres: 541.4 },
  { period: 'Q2 22', resi: 966.7, nonres: 577.5 },
  { period: 'Q3 22', resi: 914.4, nonres: 614.3 },
  { period: 'Q4 22', resi: 873.0, nonres: 649.1 },
  { period: 'Q1 23', resi: 850.1, nonres: 705.9 },
  { period: 'Q2 23', resi: 859.5, nonres: 744.3 },
  { period: 'Q3 23', resi: 879.2, nonres: 760.5 },
  { period: 'Q4 23', resi: 889.8, nonres: 786.6 },
  { period: 'Q1 24', resi: 924.1, nonres: 791.7 },
  { period: 'Q2 24', resi: 938.3, nonres: 791.4 },
  { period: 'Q3 24', resi: 935.0, nonres: 800.5 },
  { period: 'Q4 24', resi: 930.1, nonres: 803.3 },
  { period: 'Q1 25', resi: 925.2, nonres: 801.5 },
  { period: 'Q2 25', resi: 915.9, nonres: 793.1 },
  { period: 'Q3 25', resi: 928.4, nonres: 778.2 },
  { period: 'Q4 25', resi: 922.7, nonres: 763.9 },
  { period: 'Q1 26', resi: 912.6, nonres: 747.0 },
  { period: 'Q2 26', resi: 875.8, nonres: 749.0 },
  { period: 'Q3 26*', resi: 859.0, nonres: 755.2 },
];

export const PUT_IN_PLACE_LATEST = {
  month: 'Jul 2026',
  resi: 859.0,
  resiYoy: -7.3,
  nonres: 755.2,
  nonresYoy: -3.3,
} as const;

/**
 * Branch logistics PPI, Q1 2019=100.
 * Diesel = WPU057303. Truck freight = WPU3012. Not mill shingle PPI.
 */
export const BRANCH_COST = [
  { period: 'Q1 19', diesel: 100.0, freight: 100.0 },
  { period: 'Q2 19', diesel: 102.8, freight: 100.1 },
  { period: 'Q3 19', diesel: 98.0, freight: 99.9 },
  { period: 'Q4 19', diesel: 102.4, freight: 100.3 },
  { period: 'Q1 20', diesel: 87.7, freight: 100.1 },
  { period: 'Q2 20', diesel: 56.1, freight: 97.2 },
  { period: 'Q3 20', diesel: 83.1, freight: 99.2 },
  { period: 'Q4 20', diesel: 93.1, freight: 101.7 },
  { period: 'Q1 21', diesel: 121.7, freight: 105.2 },
  { period: 'Q2 21', diesel: 139.5, freight: 110.0 },
  { period: 'Q3 21', diesel: 149.3, freight: 113.4 },
  { period: 'Q4 21', diesel: 165.0, freight: 119.4 },
  { period: 'Q1 22', diesel: 194.9, freight: 127.4 },
  { period: 'Q2 22', diesel: 270.5, freight: 136.5 },
  { period: 'Q3 22', diesel: 245.1, freight: 134.6 },
  { period: 'Q4 22', diesel: 244.4, freight: 132.2 },
  { period: 'Q1 23', diesel: 196.0, freight: 127.0 },
  { period: 'Q2 23', diesel: 166.0, freight: 120.8 },
  { period: 'Q3 23', diesel: 181.8, freight: 120.1 },
  { period: 'Q4 23', diesel: 173.6, freight: 120.9 },
  { period: 'Q1 24', diesel: 166.2, freight: 119.8 },
  { period: 'Q2 24', diesel: 155.1, freight: 120.4 },
  { period: 'Q3 24', diesel: 146.6, freight: 121.1 },
  { period: 'Q4 24', diesel: 140.4, freight: 119.8 },
  { period: 'Q1 25', diesel: 138.0, freight: 121.9 },
  { period: 'Q2 25', diesel: 131.0, freight: 121.0 },
  { period: 'Q3 25', diesel: 142.4, freight: 123.6 },
  { period: 'Q4 25', diesel: 137.2, freight: 124.2 },
  { period: 'Q1 26', diesel: 154.8, freight: 126.6 },
  { period: 'Q2 26', diesel: 235.4, freight: 139.0 },
  { period: 'Q3 26*', diesel: 229.9, freight: 140.2 },
];

export const BRANCH_COST_LATEST = {
  month: 'Aug 2026',
  dieselYoy: 77.8,
  freightYoy: 14.3,
} as const;

/** BLS construction employment vs JOLTS construction openings, YoY %. Q3 26 JOLTS omitted (Jul only). */
export const CONSTRUCTION_LABOR = [
  { period: 'Q1 19', empYoy: 3.6, openYoy: 38.2 },
  { period: 'Q2 19', empYoy: 3.1, openYoy: 36.8 },
  { period: 'Q3 19', empYoy: 2.6, openYoy: 12.6 },
  { period: 'Q4 19', empYoy: 2.0, openYoy: -16.1 },
  { period: 'Q1 20', empYoy: 2.2, openYoy: -14.6 },
  { period: 'Q2 20', empYoy: -7.9, openYoy: -32.1 },
  { period: 'Q3 20', empYoy: -4.1, openYoy: -19.9 },
  { period: 'Q4 20', empYoy: -2.8, openYoy: -8.5 },
  { period: 'Q1 21', empYoy: -3.0, openYoy: 11.0 },
  { period: 'Q2 21', empYoy: 7.2, openYoy: 31.3 },
  { period: 'Q3 21', empYoy: 3.1, openYoy: 43.2 },
  { period: 'Q4 21', empYoy: 3.2, openYoy: 52.7 },
  { period: 'Q1 22', empYoy: 4.1, openYoy: 38.4 },
  { period: 'Q2 22', empYoy: 4.6, openYoy: 31.9 },
  { period: 'Q3 22', empYoy: 4.9, openYoy: 4.3 },
  { period: 'Q4 22', empYoy: 4.1, openYoy: -3.6 },
  { period: 'Q1 23', empYoy: 3.8, openYoy: -22.5 },
  { period: 'Q2 23', empYoy: 3.1, openYoy: -5.6 },
  { period: 'Q3 23', empYoy: 2.9, openYoy: 2.8 },
  { period: 'Q4 23', empYoy: 2.9, openYoy: 14.8 },
  { period: 'Q1 24', empYoy: 2.6, openYoy: 18.2 },
  { period: 'Q2 24', empYoy: 2.6, openYoy: -14.7 },
  { period: 'Q3 24', empYoy: 2.4, openYoy: -30.8 },
  { period: 'Q4 24', empYoy: 2.2, openYoy: -52.2 },
  { period: 'Q1 25', empYoy: 1.6, openYoy: -33.4 },
  { period: 'Q2 25', empYoy: 1.1, openYoy: -35.1 },
  { period: 'Q3 25', empYoy: 0.2, openYoy: -13.5 },
  { period: 'Q4 25', empYoy: -0.0, openYoy: 20.4 },
  { period: 'Q1 26', empYoy: 0.5, openYoy: -13.1 },
  { period: 'Q2 26', empYoy: 0.6, openYoy: 30.9 },
  { period: 'Q3 26*', empYoy: 1.2, openYoy: null as number | null },
];

export const LABOR_LATEST = {
  empMonth: 'Aug 2026',
  emp: 8359,
  empYoy: 1.5,
  openMonth: 'Jul 2026',
  open: 326,
  openYoy: 6.9,
} as const;

/** Harvard JCHS LIRA, Jul 23 2026 vintage. Model path, not a Census print. */
export const LIRA = {
  vintage: 'Jul 23 2026',
  mid2027Yoy: 0.5,
  mid2027SpendBn: 519,
  next: '22 Oct 2026',
  note: 'Owner-occupied improvement and repair spending. Forecast of YoY %, not ARMA squares.',
} as const;

export const DEEP_KPIS = [
  {
    id: 'sf',
    label: 'Houses started, Aug 26',
    value: '918k',
    hint: 'Annualized · +5.2% vs last year',
    tone: 'success' as const,
  },
  {
    id: 'mf',
    label: 'Apartments started, Aug 26',
    value: '344k',
    hint: 'Buildings with 5+ units · −15.5% vs last year',
    tone: 'danger' as const,
  },
  {
    id: 'mos',
    label: 'Unsold new houses, Jul 26',
    value: '9.6 mo',
    hint: 'Months of supply · ~6 is historically tight',
    tone: 'warning' as const,
  },
  {
    id: 'backlog',
    label: 'Permitted, not started, Aug 26',
    value: '276k',
    hint: 'A count of homes, not a yearly rate · +12.6% vs last year',
    tone: 'warning' as const,
  },
  {
    id: 'diesel',
    label: 'Diesel cost, Aug 26',
    value: '+77.8%',
    hint: 'vs last year · truck freight +14.3%',
    tone: 'danger' as const,
  },
  {
    id: 'pip',
    label: 'Residential construction $, Jul 26',
    value: '$859B',
    hint: 'Annualized · −7.3% vs last year · commercial $755B',
    tone: 'warning' as const,
  },
];
